/**
 * Enhanced digit conversion utilities for Persian/English/Arabic-Indic digits
 * Supports: English (0-9), Persian (۰-۹), Arabic-Indic (٠-٩)
 */

// Digit mapping arrays
const ENGLISH_DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];
const ARABIC_INDIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];

// Unicode ranges for different digit types
const PERSIAN_DIGIT_RANGE = /[\u06F0-\u06F9]/g; // Persian digits ۰-۹
const ARABIC_INDIC_DIGIT_RANGE = /[\u0660-\u0669]/g; // Arabic-Indic digits ٠-٩
const ENGLISH_DIGIT_RANGE = /[0-9]/g; // English digits 0-9

/**
 * Convert any string to Persian digits for UI display
 * @param {string|number} input - Input to convert
 * @returns {string} String with Persian digits
 */
export const toPersianDigits = (input) => {
  if (input === null || input === undefined || input === '') return '';
  
  const numStr = String(input);
  
  // Convert English digits to Persian
  let result = numStr.replace(ENGLISH_DIGIT_RANGE, (digit) => {
    const index = ENGLISH_DIGITS.indexOf(digit);
    return index !== -1 ? PERSIAN_DIGITS[index] : digit;
  });
  
  // Convert Arabic-Indic digits to Persian
  result = result.replace(ARABIC_INDIC_DIGIT_RANGE, (digit) => {
    const index = ARABIC_INDIC_DIGITS.indexOf(digit);
    return index !== -1 ? PERSIAN_DIGITS[index] : digit;
  });
  
  return result;
};

/**
 * Convert any string to English digits for backend storage
 * @param {string|number} input - Input to convert
 * @returns {string} String with English digits
 */
export const toEnglishDigits = (input) => {
  if (input === null || input === undefined || input === '') return '';
  
  const str = String(input);
  
  // Convert Persian digits to English
  let result = str.replace(PERSIAN_DIGIT_RANGE, (digit) => {
    const index = PERSIAN_DIGITS.indexOf(digit);
    return index !== -1 ? ENGLISH_DIGITS[index] : digit;
  });
  
  // Convert Arabic-Indic digits to English
  result = result.replace(ARABIC_INDIC_DIGIT_RANGE, (digit) => {
    const index = ARABIC_INDIC_DIGITS.indexOf(digit);
    return index !== -1 ? ENGLISH_DIGITS[index] : digit;
  });
  
  return result;
};

/**
 * Check if a string contains non-English digits
 * @param {string} input - Input to check
 * @returns {boolean} True if contains Persian or Arabic-Indic digits
 */
export const hasNonEnglishDigits = (input) => {
  if (!input || typeof input !== 'string') return false;
  return PERSIAN_DIGIT_RANGE.test(input) || ARABIC_INDIC_DIGIT_RANGE.test(input);
};

/**
 * Normalize numeric input for form handling
 * Removes non-digit characters except decimal point and minus sign
 * @param {string} input - Input to normalize
 * @returns {string} Normalized numeric string
 */
export const normalizeNumericInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // Convert to English digits first
  let normalized = toEnglishDigits(input);
  
  // Keep only digits, decimal point, and minus sign
  normalized = normalized.replace(/[^\d.-]/g, '');
  
  // Ensure only one decimal point
  const parts = normalized.split('.');
  if (parts.length > 2) {
    normalized = parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Ensure minus sign only at the beginning
  if (normalized.includes('-')) {
    normalized = '-' + normalized.replace(/-/g, '');
  }
  
  return normalized;
};

/**
 * Format number for display with Persian digits and thousand separators
 * @param {string|number} input - Input to format
 * @param {boolean} useThousandSeparator - Whether to add thousand separators
 * @returns {string} Formatted string with Persian digits
 */
export const formatNumberForDisplay = (input, useThousandSeparator = false) => {
  if (input === null || input === undefined || input === '') return '';
  
  const normalized = toEnglishDigits(String(input));
  
  if (useThousandSeparator && /^\d+$/.test(normalized)) {
    const formatted = normalized.replace(/\B(?=(\d{3})+(?!\d))/g, '،');
    return toPersianDigits(formatted);
  }
  
  return toPersianDigits(normalized);
};

/**
 * Parse numeric input from form field
 * Handles Persian/Arabic-Indic digits and returns clean numeric string
 * @param {string} input - Input from form field
 * @returns {string} Clean numeric string for backend
 */
export const parseNumericInput = (input) => {
  if (!input || typeof input !== 'string') return '';
  
  // Convert all digits to English
  const englishDigits = toEnglishDigits(input);
  
  // Remove any non-numeric characters except decimal point and minus
  const clean = englishDigits.replace(/[^\d.-]/g, '');
  
  // Handle decimal point
  const parts = clean.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Handle negative numbers
  if (clean.startsWith('-')) {
    return '-' + clean.substring(1).replace(/-/g, '');
  }
  
  return clean;
};

/**
 * Format number value for display in input field with Persian digits and commas
 * Use this for the `value` prop of TextField
 * @param {string|number} value - Numeric value to format
 * @returns {string} Formatted string with Persian digits and commas (e.g., "۱,۰۰۰,۰۰۰")
 */
export const formatNumberInputValue = (value) => {
  if (value === null || value === undefined || value === '') return '';
  
  // Convert to string and remove any existing commas
  const numStr = String(value).replace(/[،,]/g, '');
  const num = parseFloat(numStr);
  
  if (isNaN(num) || num === 0) return '';
  
  // Format with commas using Intl.NumberFormat
  const formatted = new Intl.NumberFormat('fa-IR').format(num);
  // Convert to Persian digits
  return toPersianDigits(formatted);
};

/**
 * Parse numeric input from user typing (removes commas, converts Persian to English)
 * Use this in the `onChange` handler of TextField
 * @param {string} input - Raw input from user
 * @param {boolean} allowDecimals - Whether to allow decimal numbers (default: false for integers)
 * @returns {string|number} Clean numeric value (string if allowDecimals, number if integer)
 */
export const parseNumberInput = (input, allowDecimals = false) => {
  if (!input || typeof input !== 'string') return allowDecimals ? '' : '';
  
  // Remove commas (both Persian and English)
  const withoutCommas = input.replace(/[،,]/g, '');
  
  // Convert Persian/Arabic digits to English
  const englishDigits = toEnglishDigits(withoutCommas);
  
  // Parse numeric value
  const parsed = parseNumericInput(englishDigits);
  
  if (!parsed) return allowDecimals ? '' : '';
  
  if (allowDecimals) {
    return parsed; // Return as string to preserve decimals
  } else {
    // Return as integer (number)
    const intValue = parseInt(parsed, 10);
    return isNaN(intValue) ? '' : intValue;
  }
};

/**
 * Create onChange handler for formatted number input fields
 * Returns an object with value formatter and onChange handler
 * @param {function} handleChange - Function to update state: (field, value) => void
 * @param {string} fieldName - Name of the field
 * @param {boolean} allowDecimals - Whether to allow decimal numbers (default: false)
 * @returns {object} { formattedValue, onChange }
 */
export const createFormattedNumberInput = (handleChange, fieldName, allowDecimals = false) => {
  return {
    /**
     * Format current value for display
     * @param {string|number} currentValue - Current value from state
     * @returns {string} Formatted value with Persian digits and commas
     */
    formatValue: (currentValue) => formatNumberInputValue(currentValue),
    
    /**
     * Handle onChange event
     * @param {Event} e - Change event from TextField
     */
    onChange: (e) => {
      const rawValue = e.target.value;
      const parsed = parseNumberInput(rawValue, allowDecimals);
      handleChange(fieldName, parsed);
    }
  };
};
  